import { ref, computed, type Ref } from 'vue'
import type { TimeRangeOption } from './types'

export function useTimelineViewport(allDates: Ref<number[]>) {
  const timeRangeOptions: TimeRangeOption[] = [
    { value: 'all', label: 'All' },
    { value: 'year', label: 'Year' },
    { value: '6months', label: '6 months' },
    { value: 'month', label: 'Month' },
    { value: 'week', label: 'Week' },
    { value: '24h', label: '24h' },
  ]

  const selectedTimeRangeOption = ref('all')

  const filteredTimeRange = computed<[number, number]>(() => {
    const now = Date.now()
    let endTime = now
    let startTime: number

    if (allDates.value.length) {
      const allMin = Math.min(...allDates.value)
      const allMax = Math.max(...allDates.value)
      endTime = allMax > now ? allMax : now

      if (selectedTimeRangeOption.value === 'all') {
        startTime = allMin - 3600000 // 1 hour buffer
        return [startTime, endTime]
      }
    }

    const hour = 3600000
    const day = hour * 24

    switch (selectedTimeRangeOption.value) {
      case 'year': {
        const d = new Date(endTime)
        d.setFullYear(d.getFullYear() - 1)
        startTime = d.getTime()
        break
      }
      case '6months': {
        const d = new Date(endTime)
        d.setMonth(d.getMonth() - 6)
        startTime = d.getTime()
        break
      }
      case 'month': {
        const d = new Date(endTime)
        d.setMonth(d.getMonth() - 1)
        startTime = d.getTime()
        break
      }
      case 'week':
        startTime = endTime - 7 * day
        break
      case '24h':
        startTime = endTime - 24 * hour
        break
      case 'all':
      default: {
        const d = new Date(endTime)
        d.setMonth(d.getMonth() - 1)
        startTime = d.getTime()
        break
      }
    }

    return [startTime, endTime]
  })

  const viewportMin = computed(() => {
    if (allDates.value.length) {
      return Math.min(...allDates.value) - 3600000
    }
    return filteredTimeRange.value[0]
  })

  const viewportMax = computed(() => {
    if (allDates.value.length) {
      const allMax = Math.max(...allDates.value)
      return Math.max(allMax, Date.now()) + 3600000
    }
    return filteredTimeRange.value[1]
  })

  return {
    timeRangeOptions,
    selectedTimeRangeOption,
    filteredTimeRange,
    viewportMin,
    viewportMax,
  }
}
